/**
 * The break-end chime, played with the Web Audio API: there is no sound file
 * to ship or fetch, and nothing for the Content Security Policy to block.
 * Browsers start audio only after the person has interacted with the page, so
 * the click that starts a break calls primeChime(); playChime() at the end of
 * the break then plays on the same, already running, audio context.
 */

let context: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (context) return context;
  const Ctor = window.AudioContext
    ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    context = new Ctor();
  } catch {
    return null;
  }
  return context;
}

/** From the click that starts a break. */
export function primeChime(): void {
  audioContext()?.resume().catch(() => { /* not allowed yet; playChime tries again */ });
}

/** A soft two-note chime; silent where the browser can't play it. */
export function playChime(): void {
  const ctx = audioContext();
  if (!ctx) return;
  try {
    ctx.resume().catch(() => { /* blocked: stays silent */ });
    const start = ctx.currentTime + 0.02;
    for (const [frequency, offset] of [[880, 0], [1318.5, 0.18]]) {
      const tone = ctx.createOscillator();
      const volume = ctx.createGain();
      tone.type = 'sine';
      tone.frequency.value = frequency;
      volume.gain.setValueAtTime(0, start + offset);
      volume.gain.linearRampToValueAtTime(0.18, start + offset + 0.02);
      volume.gain.exponentialRampToValueAtTime(0.0001, start + offset + 1.2);
      tone.connect(volume).connect(ctx.destination);
      tone.start(start + offset);
      tone.stop(start + offset + 1.25);
    }
  } catch {
    /* audio unavailable: the break still ends */
  }
}
